/**
 * Trade Form Component
 *
 * Enhanced with transaction types (buy, sell, deposit, withdrawal),
 * cost price option for deposits, and trade comments.
 * NOW THEME-AWARE
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { SymbolAutocomplete } from './SymbolAutocomplete';
import { SimulatePanel } from './SimulatePanel';
import { DatePicker } from '../ui/DatePicker';
import { getCurrencies } from '../../services/api/masterDataApi';

type TransactionType = 'buy' | 'sell' | 'deposit' | 'withdrawal';

interface TradeFormProps {
  onSubmit: (data: TradeFormData) => void;
  onCancel?: () => void;
  onDelete?: () => void; // New: Delete handler (for edit mode)
  loading?: boolean;
  initialValues?: TradeFormData;
  submitButtonText?: string;
  symbolReadOnly?: boolean; // When true, symbol cannot be changed (from position detail)
  instrumentName?: string; // Instrument name to display in read-only mode
  currencyAssumed?: boolean; // Currency was defaulted (no instrument currency on record)
  showDeleteButton?: boolean; // New: Show delete button in edit mode
}

export interface TradeFormData {
  symbol: string;
  trade_datetime: string;
  quantity: number;
  trade_price: number;
  currency: string;
  transaction_type: TransactionType;
  comment?: string;
}

export function TradeForm({
  onSubmit,
  onCancel,
  onDelete,
  loading = false,
  initialValues,
  submitButtonText,
  symbolReadOnly = false,
  instrumentName,
  currencyAssumed = false,
  showDeleteButton = false,
}: TradeFormProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  const buttonText = submitButtonText || t('portfolio.addTrade', { defaultValue: 'Add Trade' });

  // Get today's date in YYYY-MM-DD format for default
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [symbol, setSymbol] = useState(initialValues?.symbol || '');
  const [tradeDate, setTradeDate] = useState(
    initialValues?.trade_datetime ? initialValues.trade_datetime.split('T')[0] : getTodayDate()
  );
  const [quantity, setQuantity] = useState(
    initialValues?.quantity ? Math.abs(initialValues.quantity).toString() : ''
  );
  const [tradePrice, setTradePrice] = useState(initialValues?.trade_price?.toString() || '');
  const [currency, setCurrency] = useState(initialValues?.currency || 'USD');
  const [transactionType, setTransactionType] = useState<TransactionType>(
    initialValues?.transaction_type || 'buy'
  );
  const [comment, setComment] = useState(initialValues?.comment || '');

  // Pre-trade simulate (Q5 §8.1): only buy/sell in USD with a positive amount.
  const [simVisible, setSimVisible] = useState(false);
  const simAmountUsd = (parseFloat(quantity) || 0) * (parseFloat(tradePrice) || 0);
  const canSimulate =
    (transactionType === 'buy' || transactionType === 'sell') &&
    !!symbol.trim() &&
    currency === 'USD' &&
    simAmountUsd > 0;

  // Master data for currency dropdown
  const [currencies, setCurrencies] = useState<string[]>([]);
  const [showCurrencyPicker, setShowCurrencyPicker] = useState(false);
  const [loadingCurrencies, setLoadingCurrencies] = useState(true);

  // Fetch currencies on mount
  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const currenciesData = await getCurrencies();
        setCurrencies(currenciesData);
      } catch (error) {
        console.error('Failed to fetch currencies:', error);
        // Set fallback data
        setCurrencies(['USD', 'EUR', 'GBP', 'JPY', 'HKD', 'CNY']);
      } finally {
        setLoadingCurrencies(false);
      }
    };
    fetchCurrencies();
  }, []);

  const handleSymbolChange = (text: string) => {
    setSymbol(text);
    setErrors({ ...errors, symbol: undefined });
  };

  const handleSymbolSelect = (option: { symbol: string; currency?: string }) => {
    setSymbol(option.symbol);
    setErrors({ ...errors, symbol: undefined });
    // Update currency when symbol is selected from suggestions
    if (option.currency) {
      setCurrency(option.currency);
      setErrors({ ...errors, currency: undefined });
    }
  };

  const [errors, setErrors] = useState<{
    symbol?: string;
    tradeDate?: string;
    quantity?: string;
    tradePrice?: string;
    currency?: string;
  }>({});

  // Transaction type options for picker
  const transactionTypeOptions: { value: TransactionType; label: string; description: string }[] = [
    {
      value: 'buy',
      label: t('portfolio.buy', { defaultValue: 'Buy' }),
      description: t('portfolio.buyDescription', { defaultValue: 'Purchase shares' }),
    },
    {
      value: 'sell',
      label: t('portfolio.sell', { defaultValue: 'Sell' }),
      description: t('portfolio.sellDescription', { defaultValue: 'Sell shares' }),
    },
    {
      value: 'deposit',
      label: t('portfolio.deposit', { defaultValue: 'Deposit' }),
      description: t('portfolio.depositDescription', {
        defaultValue: 'Deposit shares into portfolio',
      }),
    },
    {
      value: 'withdrawal',
      label: t('portfolio.withdrawal', { defaultValue: 'Withdrawal' }),
      description: t('portfolio.withdrawalDescription', {
        defaultValue: 'Withdraw shares from portfolio',
      }),
    },
  ];

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    // Validate required fields
    if (!symbol.trim()) {
      newErrors.symbol = t('validation.symbolRequired', { defaultValue: 'Symbol is required' });
    }
    if (!tradeDate.trim()) {
      newErrors.tradeDate = t('validation.tradeDateRequired', {
        defaultValue: 'Trade date is required',
      });
    }
    if (!quantity || isNaN(parseFloat(quantity)) || parseFloat(quantity) <= 0) {
      newErrors.quantity = t('validation.invalidQuantity', { defaultValue: 'Invalid quantity' });
    }
    if (!tradePrice || isNaN(parseFloat(tradePrice)) || parseFloat(tradePrice) <= 0) {
      newErrors.tradePrice = t('validation.invalidPrice', { defaultValue: 'Invalid price' });
    }
    if (!currency.trim() || currency.length !== 3) {
      newErrors.currency = t('validation.invalidCurrency', {
        defaultValue: 'Invalid currency code',
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (loading) return;

    if (validateForm()) {
      const qtyNum = parseFloat(quantity);
      const priceNum = parseFloat(tradePrice);

      // Determine actual quantity based on transaction type
      let actualQuantity: number;

      switch (transactionType) {
        case 'buy':
          actualQuantity = qtyNum;
          break;
        case 'sell':
          actualQuantity = -qtyNum;
          break;
        case 'deposit':
          actualQuantity = qtyNum;
          break;
        case 'withdrawal':
          actualQuantity = -qtyNum;
          break;
        default:
          actualQuantity = qtyNum;
      }

      const formData: TradeFormData = {
        symbol: symbol.toUpperCase(),
        trade_datetime: tradeDate + 'T00:00:00',
        quantity: actualQuantity,
        trade_price: priceNum,
        currency: currency.toUpperCase(),
        transaction_type: transactionType,
        comment: comment.trim() || undefined,
      };

      onSubmit(formData);
    }
  };

  return (
    <ScrollView style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={[styles.formContainer, { backgroundColor: theme.colors.surface }]}>
        {/* Transaction Type Selection */}
        <View style={styles.fieldContainer}>
          <Text style={[styles.label, { color: theme.colors.text }]}>
            {t('portfolio.transactionType', { defaultValue: 'Transaction Type' })} *
          </Text>

          {/* Transaction Type Options - Horizontal */}
          <View style={styles.transactionOptionsContainer}>
            {transactionTypeOptions.map((option) => {
              const isSellType = option.value === 'sell' || option.value === 'withdrawal';
              const isSelected = transactionType === option.value;
              // For Bauhaus: buy/deposit = blue (secondary), sell/withdrawal = red (error)
              // For other themes: buy/deposit = primary color
              const backgroundColor = isSelected
                ? isSellType
                  ? theme.colors.error
                  : theme.colors.secondary
                : theme.colors.backgroundElevated;
              const borderColor = isSelected
                ? isSellType
                  ? theme.colors.error
                  : theme.colors.secondary
                : theme.colors.border;

              return (
                <TouchableOpacity
                  key={option.value}
                  style={[
                    styles.transactionOptionButton,
                    {
                      flex: 1,
                      backgroundColor,
                      borderColor,
                    },
                  ]}
                  onPress={() => setTransactionType(option.value)}
                  disabled={loading}
                >
                  <Text
                    style={[
                      styles.transactionOptionText,
                      {
                        color: isSelected ? theme.colors.textInverse : theme.colors.text,
                      },
                    ]}
                  >
                    {option.label}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>

        {/* Symbol Autocomplete */}
        <View style={styles.fieldContainer}>
          <Text style={[styles.label, { color: theme.colors.text }]}>
            {t('portfolio.symbol', { defaultValue: 'Symbol' })} *
          </Text>
          <SymbolAutocomplete
            value={symbol}
            onChangeText={handleSymbolChange}
            onSymbolSelect={handleSymbolSelect}
            placeholder={t('portfolio.symbolPlaceholder', { defaultValue: 'e.g., AAPL' })}
            error={errors.symbol}
            editable={!loading && !symbolReadOnly}
            initialCurrency={initialValues?.currency}
            readOnly={symbolReadOnly}
            instrumentName={instrumentName}
          />
        </View>

        {/* Trade Date Picker */}
        <View style={styles.fieldContainer}>
          <Text style={[styles.label, { color: theme.colors.text }]}>
            {t('portfolio.tradeDate', { defaultValue: 'Trade Date' })} *
          </Text>
          <DatePicker
            value={tradeDate}
            onChange={(date) => {
              setTradeDate(date || '');
              setErrors({ ...errors, tradeDate: undefined });
            }}
            placeholder={t('portfolio.selectDate', { defaultValue: 'Select date' })}
          />
          {errors.tradeDate && (
            <Text style={[styles.errorText, { color: theme.colors.error }]}>
              {errors.tradeDate}
            </Text>
          )}
        </View>

        {/* Quantity Input */}
        <View style={styles.fieldContainer}>
          <Text style={[styles.label, { color: theme.colors.text }]}>
            {t('portfolio.quantity', { defaultValue: 'Quantity' })} *
          </Text>
          <TextInput
            style={[
              styles.input,
              {
                backgroundColor: theme.colors.backgroundElevated,
                borderColor: theme.colors.border,
                color: theme.colors.text,
              },
              errors.quantity && styles.inputError,
            ]}
            value={quantity}
            onChangeText={(text) => {
              setQuantity(text);
              setErrors({ ...errors, quantity: undefined });
            }}
            placeholder={t('portfolio.quantityPlaceholder', { defaultValue: 'e.g., 50' })}
            placeholderTextColor={theme.colors.textTertiary}
            keyboardType="decimal-pad"
            editable={!loading}
          />
          {errors.quantity && (
            <Text style={[styles.errorText, { color: theme.colors.error }]}>{errors.quantity}</Text>
          )}
        </View>

        {/* Trade Price Input */}
        <View style={styles.fieldContainer}>
          <Text style={[styles.label, { color: theme.colors.text }]}>
            {t('portfolio.tradePrice', { defaultValue: 'Trade Price' })} *
          </Text>
          <TextInput
            style={[
              styles.input,
              {
                backgroundColor: theme.colors.backgroundElevated,
                borderColor: theme.colors.border,
                color: theme.colors.text,
              },
              errors.tradePrice && styles.inputError,
            ]}
            value={tradePrice}
            onChangeText={(text) => {
              setTradePrice(text);
              setErrors({ ...errors, tradePrice: undefined });
            }}
            placeholder={t('portfolio.tradePricePlaceholder', { defaultValue: 'e.g., 2800.00' })}
            placeholderTextColor={theme.colors.textTertiary}
            keyboardType="decimal-pad"
            editable={!loading}
          />
          {errors.tradePrice && (
            <Text style={[styles.errorText, { color: theme.colors.error }]}>
              {errors.tradePrice}
            </Text>
          )}
        </View>

        {/* Comment Input (Optional) */}
        <View style={styles.fieldContainer}>
          <Text style={[styles.label, { color: theme.colors.text }]}>
            {t('portfolio.comment', { defaultValue: 'Comment' })} (
            {t('common.optional', { defaultValue: 'Optional' })})
          </Text>
          <TextInput
            style={[
              styles.textArea,
              {
                backgroundColor: theme.colors.backgroundElevated,
                borderColor: theme.colors.border,
                color: theme.colors.text,
              },
            ]}
            value={comment}
            onChangeText={setComment}
            placeholder={t('portfolio.commentPlaceholder', {
              defaultValue: 'Add notes about this trade...',
            })}
            placeholderTextColor={theme.colors.textTertiary}
            multiline
            numberOfLines={3}
            maxLength={2000}
            editable={!loading}
            textAlignVertical="top"
          />
          <Text style={[styles.characterCount, { color: theme.colors.textTertiary }]}>
            {comment.length}/2000
          </Text>
        </View>

        {/* Currency Dropdown */}
        <View style={styles.fieldContainer}>
          <Text style={[styles.label, { color: theme.colors.text }]}>
            {t('portfolio.currency', { defaultValue: 'Currency' })} *
          </Text>
          {loadingCurrencies ? (
            <View
              style={[
                styles.pickerInput,
                {
                  backgroundColor: theme.colors.backgroundElevated,
                  borderColor: theme.colors.border,
                },
              ]}
            >
              <ActivityIndicator size="small" color={theme.colors.textSecondary} />
            </View>
          ) : (
            <TouchableOpacity
              style={[
                styles.pickerInput,
                {
                  backgroundColor: theme.colors.backgroundElevated,
                  borderColor: errors.currency ? theme.colors.error : theme.colors.border,
                },
              ]}
              onPress={() => setShowCurrencyPicker(true)}
              activeOpacity={0.7}
              disabled={loading}
            >
              <Text style={[styles.pickerText, { color: theme.colors.text }]}>{currency}</Text>
              <Text style={[styles.pickerArrow, { color: theme.colors.textSecondary }]}>▼</Text>
            </TouchableOpacity>
          )}
          {errors.currency && (
            <Text style={[styles.errorText, { color: theme.colors.error }]}>{errors.currency}</Text>
          )}
          {currencyAssumed && !errors.currency && (
            <Text style={[styles.helperText, { color: theme.colors.textSecondary }]}>
              {t('portfolio.currencyAssumed', {
                defaultValue:
                  'No currency on record for this symbol — defaulted to USD. Please verify.',
              })}
            </Text>
          )}

          {showCurrencyPicker ? (
            <View
              style={[
                styles.pickerModal,
                { backgroundColor: theme.colors.surface, borderColor: theme.colors.border },
              ]}
            >
              <ScrollView style={styles.pickerScroll} keyboardShouldPersistTaps="handled">
                {currencies.map((ccy) => (
                  <TouchableOpacity
                    key={ccy}
                    style={[styles.pickerOption, { borderBottomColor: theme.colors.border }]}
                    onPress={() => {
                      setCurrency(ccy);
                      setShowCurrencyPicker(false);
                      setErrors({ ...errors, currency: undefined });
                    }}
                  >
                    <Text
                      style={[
                        styles.pickerOptionText,
                        {
                          color: theme.colors.text,
                          fontWeight: currency === ccy ? 'bold' : 'normal',
                        },
                      ]}
                    >
                      {ccy}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
              <TouchableOpacity
                style={[styles.pickerClose, { backgroundColor: theme.colors.background }]}
                onPress={() => setShowCurrencyPicker(false)}
              >
                <Text style={[styles.pickerCloseText, { color: theme.colors.text }]}>
                  {t('common.close', { defaultValue: 'Close' })}
                </Text>
              </TouchableOpacity>
            </View>
          ) : null}
        </View>

        {/* Submit Button */}
        <View style={styles.buttonContainer}>
          <TouchableOpacity
            style={[
              styles.button,
              {
                backgroundColor:
                  !symbol || !tradeDate || !quantity || !tradePrice || !currency || loading
                    ? theme.colors.border
                    : theme.colors.primary,
              },
            ]}
            onPress={handleSubmit}
            disabled={!symbol || !tradeDate || !quantity || !tradePrice || !currency || loading}
          >
            <Text
              style={[
                styles.buttonText,
                {
                  color:
                    !symbol || !tradeDate || !quantity || !tradePrice || !currency || loading
                      ? theme.colors.textTertiary
                      : theme.colors.textInverse,
                },
              ]}
            >
              {loading ? t('common.saving', { defaultValue: 'Saving...' }) : buttonText}
            </Text>
          </TouchableOpacity>

          {/* Cancel Button */}
          {onCancel && (
            <TouchableOpacity
              style={[
                styles.button,
                {
                  backgroundColor: theme.colors.backgroundElevated,
                  borderWidth: 1,
                  borderColor: theme.colors.border,
                },
              ]}
              onPress={onCancel}
              disabled={loading}
            >
              <Text style={[styles.buttonText, { color: theme.colors.textSecondary }]}>
                {t('common.cancel')}
              </Text>
            </TouchableOpacity>
          )}

          {/* Simulate impact button (Q5 §8.1) */}
          <TouchableOpacity
            style={[
              styles.button,
              {
                backgroundColor: theme.colors.backgroundElevated,
                borderWidth: 1,
                borderColor: canSimulate ? theme.colors.primary : theme.colors.border,
              },
            ]}
            onPress={() => setSimVisible(true)}
            disabled={!canSimulate || loading}
          >
            <Text
              style={[
                styles.buttonText,
                { color: canSimulate ? theme.colors.primary : theme.colors.textTertiary },
              ]}
            >
              {t('simulate.run')}
            </Text>
          </TouchableOpacity>

          {/* Delete Button (only shown in edit mode) */}
          {showDeleteButton && onDelete && (
            <TouchableOpacity
              style={[
                styles.button,
                styles.deleteButton,
                {
                  backgroundColor: loading ? theme.colors.border : theme.colors.error,
                },
              ]}
              onPress={onDelete}
              disabled={loading}
            >
              <Text
                style={[
                  styles.buttonText,
                  loading && { color: theme.colors.textTertiary },
                  styles.deleteButtonText,
                ]}
              >
                {loading ? t('common.saving') : t('common.delete')}
              </Text>
            </TouchableOpacity>
          )}
        </View>

        <SimulatePanel
          visible={simVisible}
          onClose={() => setSimVisible(false)}
          action={transactionType === 'sell' ? 'sell' : 'buy'}
          symbol={symbol.trim().toUpperCase()}
          amountUsd={simAmountUsd}
        />
      </View>
    </ScrollView>
  );
}

/**
 * Create themed styles dynamically
 */
function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      flex: 1,
    },
    formContainer: {
      borderRadius: theme.radii.lg,
      padding: 16,
      margin: 16,
      borderWidth: theme.id === 'bauhaus' ? 2 : 0,
      borderColor: theme.colors.border,
    },
    fieldContainer: {
      marginBottom: 16,
    },
    label: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      marginBottom: 8,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
      letterSpacing: theme.id === 'bauhaus' ? 1 : 0,
    },
    input: {
      borderWidth: 1,
      borderRadius: theme.radii.md,
      padding: 12,
      fontSize: theme.typography.fontSizeMd,
    },
    textArea: {
      borderWidth: 1,
      borderRadius: theme.radii.md,
      padding: 12,
      fontSize: theme.typography.fontSizeMd,
      minHeight: 80,
    },
    inputError: {
      borderColor: theme.colors.error,
    },
    errorText: {
      fontSize: theme.typography.fontSizeXs,
      marginTop: 4,
    },
    helperText: {
      fontSize: theme.typography.fontSizeXs,
      marginTop: 4,
    },
    characterCount: {
      fontSize: theme.typography.fontSizeXs,
      marginTop: 4,
      textAlign: 'right',
    },
    // Transaction type options
    transactionOptionsContainer: {
      flexDirection: 'row',
      gap: 8,
    },
    transactionOptionButton: {
      flex: 1,
      paddingVertical: 12,
      borderRadius: theme.radii.md,
      borderWidth: 1,
      alignItems: 'center',
    },
    transactionOptionText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    // Currency picker styles
    pickerInput: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderWidth: 1,
      borderRadius: theme.radii.md,
      padding: 12,
      minHeight: 48,
    },
    pickerText: {
      fontSize: theme.typography.fontSizeMd,
    },
    pickerArrow: {
      fontSize: 12,
    },
    pickerModal: {
      marginTop: 4,
      borderRadius: theme.radii.md,
      borderWidth: 1,
      maxHeight: 200,
      elevation: 5,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.25,
      shadowRadius: 4,
    },
    pickerScroll: {
      maxHeight: 160,
    },
    pickerOption: {
      paddingVertical: 12,
      paddingHorizontal: 16,
      borderBottomWidth: 1,
    },
    pickerOptionText: {
      fontSize: theme.typography.fontSizeMd,
    },
    pickerClose: {
      paddingVertical: 12,
      alignItems: 'center',
      borderTopWidth: 1,
      borderTopColor: theme.colors.border,
    },
    pickerCloseText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    buttonContainer: {
      marginTop: 8,
      gap: 12,
    },
    button: {
      paddingVertical: 14,
      paddingHorizontal: 24,
      borderRadius: theme.radii.md,
      alignItems: 'center',
      minHeight: 48,
      justifyContent: 'center',
    },
    buttonText: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    deleteButton: {
      borderColor: theme.colors.error,
      borderWidth: 1,
    },
    deleteButtonText: {
      color: theme.colors.textInverse,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
  });
}
